/**
 * Inquiry List Page
 *
 * User's inquiry dashboard with list of all inquiries
 * Task: T5.2.4 - Inquiry List page
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  Badge,
  Select,
  Box,
  Spinner,
  EmptyState,
} from '@shopify/polaris';
import { listInquiries, type InquiryListItem, type InquiryStatus } from '../../services/inquiry.service';

const STATUS_BADGES: Record<InquiryStatus, { tone: 'info' | 'warning' | 'success' | 'critical' | 'attention'; label: string }> = {
  PENDING: { tone: 'info', label: 'Pending' },
  UNDER_REVIEW: { tone: 'attention', label: 'Under Review' },
  QUOTE_SENT: { tone: 'warning', label: 'Quote Sent' },
  QUOTE_ACCEPTED: { tone: 'success', label: 'Quote Accepted' },
  QUOTE_DECLINED: { tone: 'critical', label: 'Quote Declined' },
  QUOTE_REVISION_REQUESTED: { tone: 'attention', label: 'Revision Requested' },
  IN_PROGRESS: { tone: 'info', label: 'In Progress' },
  COMPLETED: { tone: 'success', label: 'Completed' },
  CANCELLED: { tone: 'critical', label: 'Cancelled' },
};

export function InquiryListPage() {
  const navigate = useNavigate();
  const [inquiries, setInquiries] = useState<InquiryListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');

  const fetchInquiries = useCallback(async () => {
    setLoading(true);
    try {
      const result = await listInquiries({
        page,
        limit: 10,
        status: statusFilter as InquiryStatus || undefined,
        type: typeFilter as 'SAMPLE_REQUEST' | 'BULK_ORDER' || undefined,
      });
      setInquiries(result.data ?? []);
      const total = result.meta?.total ?? 0;
      const limit = result.meta?.limit ?? 10;
      setTotalPages(Math.ceil(total / limit) || 1);
    } catch (error) {
      console.error('Failed to fetch inquiries:', error);
      setInquiries([]);
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, typeFilter]);

  useEffect(() => {
    fetchInquiries();
  }, [fetchInquiries]);

  const statusOptions = [
    { label: 'All Statuses', value: '' },
    { label: 'Pending', value: 'PENDING' },
    { label: 'Under Review', value: 'UNDER_REVIEW' },
    { label: 'Quote Sent', value: 'QUOTE_SENT' },
    { label: 'Quote Accepted', value: 'QUOTE_ACCEPTED' },
    { label: 'Quote Declined', value: 'QUOTE_DECLINED' },
    { label: 'In Progress', value: 'IN_PROGRESS' },
    { label: 'Completed', value: 'COMPLETED' },
  ];

  const typeOptions = [
    { label: 'All Types', value: '' },
    { label: 'Sample Requests', value: 'SAMPLE_REQUEST' },
    { label: 'Bulk Orders', value: 'BULK_ORDER' },
  ];

  if (loading && inquiries.length === 0) {
    return (
      <Page title="My Inquiries">
        <Box padding="800">
          <BlockStack align="center">
            <Spinner size="large" />
            <Text as="p">Loading inquiries...</Text>
          </BlockStack>
        </Box>
      </Page>
    );
  }

  return (
    <Page
      title="My Inquiries"
      subtitle="Track your sample requests and bulk order inquiries"
      primaryAction={{
        content: 'New Bulk Order',
        onAction: () => navigate('/bulk-order'),
      }}
    >
      <Layout>
        {/* Filters */}
        <Layout.Section>
          <Card>
            <InlineStack gap="400">
              <Box minWidth="150px">
                <Select
                  label="Type"
                  labelHidden
                  options={typeOptions}
                  value={typeFilter}
                  onChange={(value) => {
                    setTypeFilter(value);
                    setPage(1);
                  }}
                />
              </Box>
              <Box minWidth="150px">
                <Select
                  label="Status"
                  labelHidden
                  options={statusOptions}
                  value={statusFilter}
                  onChange={(value) => {
                    setStatusFilter(value);
                    setPage(1);
                  }}
                />
              </Box>
            </InlineStack>
          </Card>
        </Layout.Section>

        {/* Inquiry List */}
        <Layout.Section>
          {inquiries.length === 0 ? (
            <Card>
              <EmptyState
                heading="No inquiries found"
                image="/images/empty-inquiries.svg"
                action={{
                  content: 'Browse Products',
                  onAction: () => navigate('/categories'),
                }}
              >
                <p>Start by browsing our products and submitting a sample request or bulk order.</p>
              </EmptyState>
            </Card>
          ) : (
            <BlockStack gap="400">
              {inquiries.map((inquiry) => (
                <InquiryCard
                  key={inquiry.id}
                  inquiry={inquiry}
                  onClick={() => navigate(`/inquiries/${inquiry.id}`)}
                />
              ))}

              {/* Pagination */}
              {totalPages > 1 && (
                <Card>
                  <InlineStack align="center" gap="200">
                    <Button
                      disabled={page <= 1}
                      onClick={() => setPage(page - 1)}
                    >
                      Previous
                    </Button>
                    <Text as="span" variant="bodyMd">
                      Page {page} of {totalPages}
                    </Text>
                    <Button
                      disabled={page >= totalPages}
                      onClick={() => setPage(page + 1)}
                    >
                      Next
                    </Button>
                  </InlineStack>
                </Card>
              )}
            </BlockStack>
          )}
        </Layout.Section>
      </Layout>
    </Page>
  );
}

interface InquiryCardProps {
  inquiry: InquiryListItem;
  onClick: () => void;
}

function InquiryCard({ inquiry, onClick }: InquiryCardProps) {
  const statusBadge = STATUS_BADGES[inquiry.status];

  return (
    <Card>
      <div onClick={onClick} style={{ cursor: 'pointer' }}>
        <BlockStack gap="300">
          <InlineStack align="space-between" blockAlign="center">
            <BlockStack gap="100">
              <Text as="h3" variant="headingSm">
                {inquiry.referenceNo}
              </Text>
              <Text as="p" variant="bodySm" tone="subdued">
                {new Date(inquiry.createdAt).toLocaleDateString('en-IN', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                })}
              </Text>
            </BlockStack>

            <InlineStack gap="200">
              <Badge tone={inquiry.type === 'SAMPLE_REQUEST' ? 'info' : 'attention'}>
                {inquiry.type === 'SAMPLE_REQUEST' ? 'Sample' : 'Bulk Order'}
              </Badge>
              <Badge tone={statusBadge.tone}>{statusBadge.label}</Badge>
            </InlineStack>
          </InlineStack>

          <InlineStack align="space-between" blockAlign="center">
            <Text as="p" variant="bodySm">
              {inquiry.itemCount} {inquiry.itemCount === 1 ? 'item' : 'items'}
            </Text>

            {inquiry.hasQuote && (
              <Badge tone="success">Quote Available</Badge>
            )}
          </InlineStack>
        </BlockStack>
      </div>
    </Card>
  );
}
